import React, { useState } from 'react';
import styled from 'styled-components';
import { Bookmark } from 'lucide-react';
import { useRememberedEvents } from '../../contexts/RememberedEventsContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { borderRadius, colors, spacing, typography } from '../../styles/theme';

const Button = styled.button.withConfig({
  shouldForwardProp: prop => !['isRemembered', 'appearance', 'compact'].includes(prop),
})<{ isRemembered: boolean; appearance: 'default' | 'inverse'; compact: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  min-width: ${props => props.compact ? '44px' : 'auto'};
  min-height: 44px;
  padding: 0 ${props => props.compact ? spacing[2] : spacing[3]};
  border: 1px solid ${props => props.appearance === 'inverse' ? colors.white : colors.primary[300]};
  border-radius: ${borderRadius.md};
  background: ${props => props.appearance === 'inverse'
    ? (props.isRemembered ? colors.white : 'transparent')
    : (props.isRemembered ? colors.primary[100] : colors.white)};
  color: ${props => props.appearance === 'inverse'
    ? (props.isRemembered ? colors.primary[700] : colors.white)
    : colors.primary[700]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${props => props.appearance === 'inverse' ? colors.white : colors.primary[50]};
    color: ${colors.primary[700]};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.appearance === 'inverse' ? colors.white : colors.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

const LiveMessage = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export interface RememberButtonProps {
  eventId: number;
  actionLocation: string;
  compact?: boolean;
  appearance?: 'default' | 'inverse';
  className?: string;
}

const RememberButton: React.FC<RememberButtonProps> = ({
  eventId,
  actionLocation,
  compact = false,
  appearance = 'default',
  className,
}) => {
  const rememberedEvents = useRememberedEvents();
  const { trackEvent } = useAnalytics();
  const [message, setMessage] = useState('');
  const isRemembered = rememberedEvents.isRemembered(eventId);
  const isPending = rememberedEvents.pendingEventIds.has(eventId);
  const accessibleLabel = isRemembered ? 'Remove from My Events' : 'Remember Event';

  const toggle = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (isRemembered) await rememberedEvents.forget(eventId);
      else await rememberedEvents.remember(eventId);
      setMessage(isRemembered ? 'Removed from My Events' : 'Event remembered');
      trackEvent({
        category: 'remembered_events',
        action: isRemembered ? 'forget_event' : 'remember_event',
        label: String(eventId),
        custom_parameters: { action_location: actionLocation, outcome: 'success' },
      });
    } catch (error) {
      setMessage((error as Error).message || 'My Events could not be updated');
      trackEvent({
        category: 'remembered_events',
        action: isRemembered ? 'forget_event' : 'remember_event',
        label: String(eventId),
        custom_parameters: { action_location: actionLocation, outcome: 'failed' },
      });
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        isRemembered={isRemembered}
        appearance={appearance}
        compact={compact}
        onClick={toggle}
        disabled={!rememberedEvents.isAvailable || rememberedEvents.isLoading || isPending}
        aria-label={accessibleLabel}
        aria-pressed={isRemembered}
        title={accessibleLabel}
      >
        <Bookmark aria-hidden="true" size={16} fill={isRemembered ? 'currentColor' : 'none'} />
        {!compact && (isPending ? 'Updating…' : isRemembered ? 'Remembered' : 'Remember Event')}
      </Button>
      <LiveMessage role="status" aria-live="polite">{message}</LiveMessage>
    </>
  );
};

export default RememberButton;